/**
 * ロール・部署切り替えロジック
 * - getAvailableRoles: ユーザーが切り替え可能なロール一覧を返す
 * - filterMenuGroups: 部署コード・ロール・役職レベルに基づいてメニューグループをフィルタリングする
 */

export interface MenuGroupForFilter {
  label: string
  allowedRoles?: string[]
  allowedDepartmentCodes?: string[]
  minRoleLevel?: number
  hidden?: boolean
}

/**
 * ユーザーが選択可能なロール一覧を返す。
 * - primary role が先頭
 * - available_roles（兼務ロール）が続く
 * - 重複除去済み
 */
export function getAvailableRoles(
  primaryRole: string | null | undefined,
  availableRoles: string[] | null | undefined
): string[] {
  if (!primaryRole) return []
  const extra = (availableRoles ?? []).filter((r) => r !== primaryRole)
  return [primaryRole, ...extra]
}

/**
 * 部署コード・ロール・役職レベル・hidden フラグでメニューグループをフィルタリングする。
 *
 * 優先順位:
 * 1. hidden: true → 除外
 * 2. allowedDepartmentCodes が設定済み → いずれかの部署コードが userDeptCodes に含まれれば通過
 * 3. minRoleLevel が設定済み → userRoleLevel <= minRoleLevel で通過（admin は常に通過）
 *    ※ level は低いほど上位（社長=0, 課長=6, スタッフ=8）
 * 4. allowedRoles が設定済み（後方互換）→ ロール一致で通過
 * 5. いずれも未設定 → 常に通過
 *
 * adminRole='admin' のユーザーはすべての制限をバイパスする。
 */
export function filterMenuGroups<T extends MenuGroupForFilter>(
  groups: T[],
  activeRole: string | null | undefined,
  allRoles?: string[],
  userDeptCodes?: string[] | null,
  userRoleLevel?: number | null
): T[] {
  const isAdmin = activeRole === 'admin' || (allRoles ?? []).includes('admin')
  const roles = allRoles && allRoles.length > 0 ? allRoles : activeRole ? [activeRole] : []
  const deptCodes = userDeptCodes ?? []

  return groups.filter((group) => {
    if (group.hidden) return false

    // admin はすべてのグループに無条件でアクセス可
    if (isAdmin) return true

    // 部署コード制御
    if (group.allowedDepartmentCodes && group.allowedDepartmentCodes.length > 0) {
      return group.allowedDepartmentCodes.some((c) => deptCodes.includes(c))
    }

    // 役職レベル制御（level は低いほど上位。課長=6 以下なら通過）
    if (group.minRoleLevel != null) {
      if (userRoleLevel == null) return false
      return userRoleLevel <= group.minRoleLevel
    }

    // 後方互換: allowedRoles のみ設定されている場合
    if (group.allowedRoles) {
      if (roles.length === 0) return false
      return roles.some((r) => group.allowedRoles!.includes(r))
    }

    // 制限なし → 全員表示
    return true
  })
}
